import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { cn } from '@kidshome/ui';
import { resolveErrorMessage, type OccurrenceResponse } from '@kidshome/api-contract';
import { tDynamic } from '@/shared/lib/i18n';
import { MissionTile } from '@/widgets/missions/MissionTile';
import { useMissionActions } from '@/widgets/missions/use-mission-actions';

/**
 * The Missions shelf (ADR-0141): a flat grid of cover tiles (NO grouping — the covers do the visual
 * work). Wires the shared optimistic complete/redo/undo once for the assignee and hands each tile its
 * per-row state. Used by the dedicated `/missions` page; the Home digest renders a featured subset.
 */
export function MissionsBoard({
  missions,
  assigneeId,
  canComplete,
  className,
}: {
  missions: readonly OccurrenceResponse[];
  assigneeId: string;
  canComplete: boolean;
  /** Extra grid classes — the Home digest uses this to keep its featured subset to ONE clean row. */
  className?: string;
}): React.ReactElement {
  const { t } = useTranslation('family');
  const navigate = useNavigate();
  const { complete, redo, uncomplete, isCompleting, isRedoing, isUndoing, error } =
    useMissionActions(assigneeId);

  // Surface a complete/redo/undo failure (after the optimistic rollback) instead of failing silently —
  // same banner as the day board (both `/missions` and the Home digest sit under a `.kid-home` root, so
  // today.css's `.occ-error` applies).
  const errorMessage = (() => {
    if (error == null) return null;
    const m = resolveErrorMessage(error);
    return tDynamic(t, m.key, m.params);
  })();

  return (
    // The banner lives OUTSIDE the grid: the digest's `nth-child` row-capping targets grid children,
    // so an in-grid banner would shift which tile gets hidden.
    <>
      {errorMessage && (
        <p role="alert" className="occ-error">
          {errorMessage}
        </p>
      )}
      <div className={cn('grid grid-cols-2 gap-3 sm:grid-cols-3', className)}>
        {missions.map((occ) => (
          <MissionTile
            key={occ.id}
            occ={occ}
            canComplete={canComplete}
            completing={isCompleting(occ.id)}
            redoing={isRedoing(occ.id)}
            undoing={isUndoing(occ.id)}
            onComplete={() => complete(occ)}
            onRedo={() => redo(occ.id)}
            onUndo={() => uncomplete(occ)}
            // A PROJECTED (virtual) mission has no chore-detail record yet — the API rejects its synthetic
            // `virtual:` id as a route param, so it stays inert (ADR-0142).
            onOpen={occ.projected ? undefined : () => void navigate(`/chore/${occ.id}`)}
          />
        ))}
      </div>
    </>
  );
}
